import { useState, useCallback, useEffect } from 'react'
import { FileText, Download, File, Image as ImageIcon, Search, X, ExternalLink } from 'lucide-react'
import type { FileCacheItem } from './types'

interface FileViewerProps {
  file: FileCacheItem
  onDownload: (file: FileCacheItem) => Promise<void>
  onPreview?: (file: FileCacheItem) => Promise<string | undefined>
  isDownloading: boolean
}

export function FileViewer({ file, onDownload, onPreview, isDownloading }: FileViewerProps) {
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const [imageError, setImageError] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const isImage = file.mimeType?.startsWith('image/') || false
  const isPdf = file.mimeType === 'application/pdf'

  useEffect(() => {
    if (!isFullscreen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = prevOverflow
    }
  }, [isFullscreen])

  const handleImageLoad = useCallback(() => {
    setImageError(false)
  }, [])

  const handleImageError = useCallback(() => {
    setImageError(true)
  }, [])

  const openDataUrlInNewTab = useCallback((dataUrl: string, mimeHint?: string) => {
    try {
      if (dataUrl.startsWith('data:')) {
        const [meta, b64] = dataUrl.split(',')
        const mime = meta.match(/data:([^;]+)/)?.[1] || mimeHint || 'application/octet-stream'
        const binary = atob(b64)
        const bytes = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
        const url = URL.createObjectURL(new Blob([bytes], { type: mime }))
        window.open(url, '_blank', 'noopener,noreferrer')
        return
      }
      window.open(dataUrl, '_blank', 'noopener,noreferrer')
    } catch {
      window.open(dataUrl, '_blank', 'noopener,noreferrer')
    }
  }, [])

  const handlePreviewClick = useCallback(async () => {
    if (isImage && file.content) {
      setIsFullscreen(true)
      return
    }
    if (file.content) {
      openDataUrlInNewTab(file.content, file.mimeType)
      return
    }
    if (!onPreview) return
    try {
      setIsPreviewLoading(true)
      const dataUrl = await onPreview(file)
      if (dataUrl) openDataUrlInNewTab(dataUrl, file.mimeType)
    } finally {
      setIsPreviewLoading(false)
    }
  }, [file, isImage, onPreview, openDataUrlInNewTab])

  const thumbnailBg = isPdf ? 'bg-red-50' : 'bg-[hsl(var(--muted))]'
  const thumbnailIcon = isPdf
    ? <FileText className="w-5 h-5 text-red-500" />
    : isImage
    ? <ImageIcon className="w-5 h-5 text-[hsl(var(--muted-foreground))]" />
    : <File className="w-5 h-5 text-[hsl(var(--muted-foreground))]" />

  return (
    <div className="flex items-center gap-3 py-3">
      {/* Thumbnail */}
      <div className={`w-[52px] h-[52px] rounded-lg ${thumbnailBg} flex items-center justify-center flex-shrink-0 overflow-hidden`}>
        {isImage && file.content && !imageError ? (
          <img
            src={file.content}
            alt={file.name}
            className="w-full h-full object-cover cursor-pointer"
            onLoad={handleImageLoad}
            onError={handleImageError}
            onClick={() => setIsFullscreen(true)}
          />
        ) : (
          thumbnailIcon
        )}
      </div>

      {/* File info */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[hsl(var(--foreground))] truncate">{file.name}</div>
        <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">
          {isPdf ? 'PDF Document' : isImage ? 'Image' : 'File'}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-1.5 flex-shrink-0">
        <button
          className="p-2 rounded-lg bg-[hsl(var(--muted))] hover:bg-[hsl(var(--border))] transition-colors disabled:opacity-50"
          onClick={handlePreviewClick}
          disabled={isPreviewLoading || (isImage && imageError)}
          aria-label={isImage ? 'View image fullscreen' : 'Open in new tab'}
        >
          {isPreviewLoading ? (
            <span className="spinner spinner-sm" />
          ) : isImage ? (
            <Search className="w-3.5 h-3.5 text-[hsl(var(--foreground))]" />
          ) : (
            <ExternalLink className="w-3.5 h-3.5 text-[hsl(var(--foreground))]" />
          )}
        </button>
        <button
          className="p-2 rounded-lg bg-[hsl(var(--muted))] hover:bg-[hsl(var(--border))] transition-colors disabled:opacity-50"
          onClick={() => onDownload(file)}
          disabled={isDownloading}
        >
          {isDownloading ? <span className="spinner spinner-sm" /> : <Download className="w-3.5 h-3.5 text-[hsl(var(--foreground))]" />}
        </button>
      </div>

      {/* Fullscreen lightbox */}
      {isFullscreen && isImage && file.content && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setIsFullscreen(false)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setIsFullscreen(false) }}
            className="absolute top-4 right-4 p-2 bg-black/50 text-white rounded-lg hover:bg-black/70 z-10"
            aria-label="Close fullscreen"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={file.content}
            alt={file.name}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  )
}

export default FileViewer
